import { StudentProduct, StudentSubscription } from "@freetech/models";
import {
  StripeProductsResponse,
  SubscriptionsResponse,
  BalanceTransactionsResponse,
} from "@freetech/models/stripe";
import { axiosClient } from "../axiosClient";

export const stripeFunctions = {
  stakeholder: {
    /**
     * Creates a Stripe checkout session for an invoice payment
     *
     * @param invoiceId The ID of the invoice to pay
     * @param clientId The ID of the client associated with the invoice
     * @param amount The amount to charge in dollars
     * @param invoiceNumber The invoice number for display purposes
     * @param successUrl The URL to redirect to after successful payment
     * @param cancelUrl The URL to redirect to if payment is cancelled
     * @returns The checkout URL, session ID, and fee information
     */
    createCheckoutSession: async (
      invoiceId: string,
      clientId: string,
      amount: number,
      invoiceNumber: string,
      successUrl: string,
      cancelUrl: string
    ): Promise<{
      checkoutUrl: string;
      sessionId: string;
      originalAmount: number;
      processingFee: number;
      totalAmount: number;
    }> => {
      return await axiosClient.post(
        "/stripe/stakeholder/create-checkout-session",
        {
          invoiceId,
          clientId,
          amount,
          invoiceNumber,
          successUrl,
          cancelUrl,
        }
      );
    },
  },
  student: {
    /**
     * Creates a Stripe subscription checkout session for a student
     *
     * @param priceId The ID of the Stripe price to subscribe to
     * @param successUrl The URL to redirect to after successful subscription
     * @param cancelUrl The URL to redirect to if subscription is cancelled
     * @param couponCode Optional coupon code to apply to the subscription
     * @returns The checkout URL and session ID
     */
    createSubscription: async (
      priceId: string,
      couponCode?: string,
      successUrl: string = `${window.location.origin}/student/dashboard?subscription=success`,
      cancelUrl: string = `${window.location.origin}/student/manage-subscription?subscription=cancelled`
    ): Promise<{
      checkoutUrl: string;
      sessionId: string;
    }> => {
      return await axiosClient.post("/stripe/student/create-subscription", {
        priceId,
        successUrl,
        cancelUrl,
        couponCode,
      });
    },

    /**
     * Gets the current subscription information for the student
     *
     * @returns The subscription information, or null if no subscription exists
     */
    getSubscription: async (): Promise<{
      hasSubscription: boolean;
      subscription: {
        id: string;
        studentId: string;
        planName: string;
        amount: number;
        status: string;
        currentPeriodStart: string;
        currentPeriodEnd: string;
        canceledAt?: string;
        cancelAtPeriodEnd: boolean;
        stripeSubscriptionId: string;
        createdAt: string;
        updatedAt: string;
      } | null;
    }> => {
      return await axiosClient.get("/stripe/student/get-subscription");
    },

    /**
     * Cancels the student's current subscription
     *
     * @param cancelImmediately Whether to cancel immediately (true) or at the end of the billing period (false)
     * @returns Success status and message
     */
    cancelSubscription: async (
      cancelImmediately: boolean = false
    ): Promise<{
      success: boolean;
      message: string;
    }> => {
      return await axiosClient.delete("/stripe/student/cancel-subscription", {
        data: { cancelImmediately },
      });
    },

    /**
     * Reactivates a previously canceled subscription
     *
     * @returns Success status and message
     */
    reactivateSubscription: async (): Promise<{
      success: boolean;
      message: string;
      subscription?: StudentSubscription;
    }> => {
      return await axiosClient.post("/stripe/student/reactivate-subscription");
    },

    /**
     * Creates a Stripe Customer Portal session for managing payment methods and subscription
     *
     * @param returnUrl The URL to return to after the customer finishes in the portal
     * @returns The URL to the customer portal
     */
    createCustomerPortalSession: async (
      returnUrl: string = `${window.location.origin}/student/manage-subscription`
    ): Promise<{
      success: boolean;
      url: string;
    }> => {
      return await axiosClient.post("/stripe/student/customer-portal", {
        returnUrl,
      });
    },

    /**
     * Create a subscription for a student with default URLs
     * @param priceId The Stripe price ID
     * @param couponCode Optional coupon code to apply to the subscription
     * @returns The checkout URL and session ID
     */
    createSubscriptionWithDefaults: async (
      priceId: string,
      couponCode?: string
    ) => {
      try {
        return await stripeFunctions.student.createSubscription(
          priceId,
          couponCode,
          `${window.location.origin}/student/dashboard?subscription=success`,
          `${window.location.origin}/student/manage-subscription?subscription=cancelled`
        );
      } catch (error) {
        console.error("Error creating subscription:", error);
        throw error;
      }
    },

    /**
     * Get the current subscription for a student (simplified return)
     * @returns The subscription information or null
     */
    getSubscriptionSimplified:
      async (): Promise<StudentSubscription | null> => {
        try {
          const data = await stripeFunctions.student.getSubscription();

          if (data && "hasSubscription" in data && "subscription" in data) {
            return data.hasSubscription
              ? (data.subscription as StudentSubscription)
              : null;
          }

          return null;
        } catch (error) {
          console.error("Error getting subscription:", error);
          return null;
        }
      },

    /**
     * Cancel a student's subscription with period end option
     * @param atPeriodEnd Whether to cancel at the end of the billing period
     * @returns The cancellation result
     */
    cancelSubscriptionWithPeriodEnd: async (
      atPeriodEnd: boolean = true
    ): Promise<{ success: boolean; message: string }> => {
      try {
        return await stripeFunctions.student.cancelSubscription(!atPeriodEnd);
      } catch (error) {
        console.error("Error canceling subscription:", error);
        throw error;
      }
    },

    /**
     * Get all available student subscription products
     * @returns The list of available products
     */
    getProducts: async (): Promise<StudentProduct[]> => {
      return await axiosClient.get("/stripe/student/get-student-products");
    },
  },
  admin: {
    /**
     * Get all active Stripe products with pricing
     * @returns Promise containing Stripe products data
     */
    getProducts: async (): Promise<StripeProductsResponse> => {
      return await axiosClient.get("/stripe/admin/get-stripe-products");
    },

    /**
     * Get Stripe subscriptions with detailed information
     * @param options Optional parameters for filtering subscriptions
     * @returns Promise containing subscription data
     */
    getSubscriptions: async (options?: {
      limit?: number;
      status?:
        | "active"
        | "past_due"
        | "unpaid"
        | "canceled"
        | "incomplete"
        | "incomplete_expired"
        | "trialing"
        | "all";
    }): Promise<SubscriptionsResponse> => {

      const queryParams = new URLSearchParams();

      if (options?.limit) {
        queryParams.append("limit", options.limit.toString());
      }

      if (options?.status) {
        queryParams.append("status", options.status);
      }

      const queryString = queryParams.toString();
      const url = `/stripe/admin/get-subscriptions${queryString ? `?${queryString}` : ""}`;

      return await axiosClient.get(url);
    },

    /**
     * Get Stripe balance transactions with detailed information
     * @param options Optional parameters for filtering transactions
     * @returns Promise containing balance transaction data
     */
    getBalanceTransactions: async (options?: {
      limit?: number;
      starting_after?: string;
      ending_before?: string;
      type?: string;
      time_range?: "7d" | "30d" | "90d" | "1y";
    }): Promise<BalanceTransactionsResponse> => {

      const queryParams = new URLSearchParams();

      if (options?.limit) {
        queryParams.append("limit", options.limit.toString());
      }

      if (options?.starting_after) {
        queryParams.append("starting_after", options.starting_after);
      }

      if (options?.ending_before) {
        queryParams.append("ending_before", options.ending_before);
      }

      if (options?.type) {
        queryParams.append("type", options.type);
      }

      if (options?.time_range) {
        queryParams.append("time_range", options.time_range);
      }

      const queryString = queryParams.toString();
      const url = `/stripe/admin/get-balance-transactions${queryString ? `?${queryString}` : ""}`;

      return await axiosClient.get(url);
    },
  },
};
